import { useRoute } from 'vue-router';
import { formatGradeName } from '@/lib/grades';
import type {
  NavigationItem,
  UseReportNavigationOptions,
} from '../types/navigation';
import {
  getIndicatorAppend,
  getGatewayAppend,
  getCriterionDisplayTitle,
} from './navigation-helpers';

export function buildGatewayNavigation(
  reportId: string,
  options: UseReportNavigationOptions,
): NavigationItem[] {
  const route = useRoute();
  const baseItems: NavigationItem[] = [];

  // Build gateway items with proper structure for gateway views only
  if (options.gradeReport) {
    const mainGateways = options.gradeReport.gateways || [];
    const overviewGateways = options.gradeReport.gateway_overviews || [];

    const gatewayMap = new Map();
    mainGateways.forEach((gateway: any) => {
      gatewayMap.set(gateway.slug, gateway);
    });
    overviewGateways.forEach((gateway: any) => {
      if (!gatewayMap.has(gateway.slug)) {
        gatewayMap.set(gateway.slug, gateway);
      }
    });

    const allGateways = Array.from(gatewayMap.values());

    // Check if we have multiple grades with the same gateway (e.g., multiple Gateway 1s)
    const gatewaysByNumber = new Map();
    allGateways.forEach((gateway: any) => {
      const isMllGateway = gateway.slug === 'multilingual-learner-supports';

      let gatewayNumber: string;
      if (isMllGateway) {
        gatewayNumber = '4';
      } else {
        // Extract gateway number from slugs like "gateway-one__second-grade" or "gateway-two"
        const match = gateway.slug.match(
          /gateway-(one|two|three|four|five|\d+)/,
        );
        if (match) {
          const numberPart = match[1];
          // Convert word numbers to digits
          const numberMap: Record<string, string> = {
            one: '1',
            two: '2',
            three: '3',
            four: '4',
            five: '5',
          };
          gatewayNumber = numberMap[numberPart] || numberPart;
        } else {
          // Fallback - try to extract from label
          const labelMatch = gateway.label?.match(/Gateway\s+(\d+)/i);
          gatewayNumber = labelMatch ? labelMatch[1] : '1';
        }
      }

      if (!gatewaysByNumber.has(gatewayNumber)) {
        gatewaysByNumber.set(gatewayNumber, []);
      }
      gatewaysByNumber.get(gatewayNumber).push(gateway);
    });

    // Check if any gateway number has multiple instances (different grades)
    const hasMultipleGradesPerGateway = Array.from(
      gatewaysByNumber.values(),
    ).some((gateways: any[]) => gateways.length > 1);

    if (allGateways.length > 0) {
      let gateway1Found = false; // Track if we've already found Gateway 1

      const gatewayItems: NavigationItem[] = [];

      // Helper function to get grade order from multiple possible sources
      const getGradeOrder = (gateway: any): number => {
        // First try the grade.order property
        if (gateway.grade?.order !== undefined) {
          return gateway.grade.order;
        }

        // Try to extract from gateway.grades array (new structure)
        if (
          gateway.grades &&
          Array.isArray(gateway.grades) &&
          gateway.grades.length > 0
        ) {
          const grade = gateway.grades[0];
          if (grade.order !== undefined) {
            return grade.order;
          }
          if (grade.shortcode) {
            // Handle Kindergarten
            if (grade.shortcode === 'K' || grade.shortcode === '0') {
              return 0;
            }
            // Handle numeric grades
            const numericMatch = grade.shortcode.match(/\d+/);
            if (numericMatch) {
              return parseInt(numericMatch[0]);
            }
          }
        }

        // Try to extract from grade name/shortcode
        const gradeName = gateway.grade?.name || gateway.grade?.shortcode || '';
        if (gradeName) {
          // Handle Kindergarten
          if (
            gradeName.toLowerCase().includes('kindergarten') ||
            gradeName === 'K' ||
            gradeName === '0'
          ) {
            return 0;
          }
          // Handle numeric grades
          const numericMatch = gradeName.match(/\d+/);
          if (numericMatch) {
            return parseInt(numericMatch[0]);
          }
        }

        // Try to extract from slug
        const slugMatch = gateway.slug?.match(/__(.+)$/);
        if (slugMatch) {
          const gradeSlug = slugMatch[1];
          if (gradeSlug.includes('kindergarten') || gradeSlug === 'k') {
            return 0;
          }
          const numericMatch = gradeSlug.match(/\d+/);
          if (numericMatch) {
            return parseInt(numericMatch[0]);
          }
        }

        // Try to extract from label
        const labelMatch = gateway.label?.match(/\((.*?)\)/);
        if (labelMatch) {
          const gradeLabel = labelMatch[1];
          if (
            gradeLabel.toLowerCase().includes('kindergarten') ||
            gradeLabel === 'K'
          ) {
            return 0;
          }
          const numericMatch = gradeLabel.match(/\d+/);
          if (numericMatch) {
            return parseInt(numericMatch[0]);
          }
        }

        // Default fallback
        return 999;
      };

      for (const [gatewayNumber, gateways] of gatewaysByNumber.entries()) {
        if (gateways.length > 1) {
          const sortedGateways = gateways.sort((a: any, b: any) => {
            const aGradeOrder = getGradeOrder(a);
            const bGradeOrder = getGradeOrder(b);

            return aGradeOrder - bGradeOrder;
          });

          // Log the final sorted order
          // console.log(
          //   `Gateway ${gatewayNumber} sorted order:`,
          //   sortedGateways.map((g: any) => ({
          //     slug: g.slug,
          //     label: g.label,
          //     grade: g.grade,
          //     gradeOrder: getGradeOrder(g),
          //   })),
          // );

          // Log the final display order for verification
          // console.log(
          //   `Gateway ${gatewayNumber} final display order:`,
          //   sortedGateways.map((g: any) => {
          //     let gradeName = 'Unknown';
          //     if (g.grades && Array.isArray(g.grades) && g.grades.length > 0) {
          //       const grade = g.grades[0];
          //       gradeName =
          //         grade.name || grade.title || grade.shortcode || grade.slug;
          //     } else if (g.grade) {
          //       gradeName = g.grade.name || g.grade.shortcode || g.grade.title;
          //     }
          //     return `${gradeName} (order: ${getGradeOrder(g)})`;
          //   }),
          // );

          const gatewayChildren = sortedGateways.map((gateway: any) => {
            let gradeTitle: string;
            let gradeSlug: string;

            if (gateway.grade?.name) {
              gradeTitle = formatGradeName(
                gateway.grade.name || gateway.grade.shortcode,
              );
              gradeSlug = gateway.grade.slug;
            } else {
              const gradeMatch = gateway.label?.match(/\((.*?)\)/);
              if (gradeMatch) {
                gradeTitle = gradeMatch[1];
                gradeSlug = gradeTitle.toLowerCase().replace(/\s+/g, '-');
              } else {
                const slugMatch = gateway.slug.match(/__(.+)$/);
                if (slugMatch) {
                  gradeSlug = slugMatch[1];
                  gradeTitle = formatGradeName(gradeSlug.replace(/-/g, ' '));
                } else {
                  gradeTitle = `Grade ${gatewayNumber}`;
                  gradeSlug = `grade-${gatewayNumber}`;
                }
              }
            }

            const gatewayWord =
              ['one', 'two', 'three', 'four', 'five'][
                parseInt(gatewayNumber) - 1
              ] || gatewayNumber;
            const routeGateway = `gateway-${gatewayWord}`;

            const criterionChildren: NavigationItem[] = [];
            const gatewayDetailSource =
              options.currentGatewayReport?.slug === gateway.slug
                ? options.currentGatewayReport
                : gateway;

            // Try to get criterion data from different sources
            let criterionData = null;

            if (gatewayDetailSource?.criterion) {
              criterionData = gatewayDetailSource.criterion;
            } else if (gatewayDetailSource?.criterion_overviews) {
              criterionData = gatewayDetailSource.criterion_overviews;
            } else if (gateway.criterion) {
              criterionData = gateway.criterion;
            } else if (gateway.criterion_overviews) {
              criterionData = gateway.criterion_overviews;
            } else {
              // Fallback to gateway overviews from gradeReport
              const matchingOverview =
                options.gradeReport?.gateway_overviews?.find(
                  (gw: any) => gw.slug === gateway.slug,
                );
              if (matchingOverview?.criterion_overviews) {
                criterionData = matchingOverview.criterion_overviews;
              }
            }

            // Use the original grade parameter for route construction (e.g., kindergarten-to-second)
            // gradeSlug is used only for internal logic, not for routes
            const finalGradeSlug = options.grade || '';

            if (criterionData && Array.isArray(criterionData)) {
              criterionData.forEach((criterion: any, index: number) => {
                const indicatorChildren =
                  criterion.indicators?.map((indicator: any) => {
                    const indicatorAppend = getIndicatorAppend(indicator);
                    return {
                      title: indicator.label,
                      value: `criterion-${gatewayWord}-${finalGradeSlug}-${index}-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`,
                      append: indicatorAppend?.text || '',
                      appendClasses: indicatorAppend?.class || '',
                      route: {
                        name: 'report-gateway',
                        params: {
                          id: reportId,
                          grade: finalGradeSlug,
                          gateway: routeGateway,
                        },
                      },
                      href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                    };
                  }) || [];

                criterionChildren.push({
                  title: getCriterionDisplayTitle(criterion, criterionData),
                  value: `criterion-${gatewayWord}-${finalGradeSlug}-${index}`,
                  isGroup: indicatorChildren.length > 0,
                  indentChildren: true,
                  children: indicatorChildren,
                  route: {
                    name: 'report-gateway',
                    params: {
                      id: reportId,
                      grade: finalGradeSlug,
                      gateway: routeGateway,
                    },
                  },
                  href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                });
              });
            } else {
              // Fallback to gateway overviews from the report
              const matchingOverview =
                options.gradeReport?.gateway_overviews?.find(
                  (gw: any) => gw.slug === gateway.slug,
                );

              if (matchingOverview?.criterion_overviews) {
                matchingOverview.criterion_overviews.forEach(
                  (criterion: any, index: number) => {
                    const indicatorChildren =
                      criterion.indicators?.map((indicator: any) => {
                        const indicatorAppend = getIndicatorAppend(indicator);
                        return {
                          title: indicator.label,
                          value: `criterion-${gatewayWord}-${finalGradeSlug}-${index}-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`,
                          append: indicatorAppend?.text || '',
                          appendClasses: indicatorAppend?.class || '',
                          route: {
                            name: 'report-gateway',
                            params: {
                              id: reportId,
                              grade: finalGradeSlug,
                              gateway: routeGateway,
                            },
                          },
                          href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                        };
                      }) || [];

                    criterionChildren.push({
                      title: getCriterionDisplayTitle(criterion, null),
                      value: `criterion-${gatewayWord}-${finalGradeSlug}-${index}`,
                      isGroup: indicatorChildren.length > 0,
                      indentChildren: true,
                      children: indicatorChildren,
                      route: {
                        name: 'report-gateway',
                        params: {
                          id: reportId,
                          grade: finalGradeSlug,
                          gateway: routeGateway,
                        },
                      },
                      href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                    });
                  },
                );
              }
            }

            const gradeChildItem = {
              title: gradeTitle,
              value: `gateway-${gatewayWord}-${gradeSlug}`,
              isGroup: criterionChildren.length > 0,
              indentChildren: true,
              children: criterionChildren,
              route: {
                name: 'report-gateway',
                params: {
                  id: reportId,
                  grade: finalGradeSlug,
                  gateway:
                    gatewayWord === 'one'
                      ? `${routeGateway}__${gradeSlug}`
                      : routeGateway,
                },
              },
              href: `#${routeGateway}`,
              // For Gateway 1: auto-open the current grade's group
              defaultOpen:
                gatewayWord === 'one' &&
                typeof route.params.gateway === 'string' &&
                route.params.gateway.includes('__') &&
                route.params.gateway.split('__')[1] === gradeSlug,
            };

            return gradeChildItem;
          });

          // console.log(
          //   `Gateway ${gatewayNumber} children routes:`,
          //   gatewayChildren.map((child: NavigationItem) => ({
          //     title: child.title,
          //     route: child.route,
          //     hasValidRoute: !!(
          //       child.route?.params?.id &&
          //       child.route?.params?.grade &&
          //       child.route?.params?.gateway
          //     ),
          //   })),
          // );

          // Determine if this gateway should be open by default
          const isGateway1 = gatewayNumber === '1' || gatewayNumber === 'one';
          let defaultOpen = false;

          // In gateway view: open Gateway 1 if we're on any Gateway 1 page
          if (isGateway1) {
            const currentGateway = route.params.gateway as string;
            defaultOpen = currentGateway?.startsWith('gateway-one');
          }

          gatewayItems.push({
            title: `Gateway ${gatewayNumber}`,
            value: `gateway-group-${gatewayNumber}`,
            isH4: true,
            isGroup: true,
            borderTop: true,
            defaultOpen,
            children: gatewayChildren,
            // No route property - this is just a group header
          });
        } else if (gateways.length > 0) {
          const gateway = gateways[0];
          const isMllGateway = gateway.slug === 'multilingual-learner-supports';
          const gatewayNumber = isMllGateway
            ? '4'
            : gateway.slug.replace('gateway-', '');
          const gradeSlug = gateway.slug.match(/__(.+)$/)?.[1];
          const gatewayWord =
            ['one', 'two', 'three', 'four', 'five'][
              parseInt(gatewayNumber) - 1
            ] || gatewayNumber;
          const routeGateway = isMllGateway
            ? 'multilingual-learner-supports'
            : gatewayWord === 'one' && gradeSlug
              ? `gateway-${gatewayWord}__${gradeSlug}` // Add grade suffix only for Gateway 1 and only if we have a grade
              : `gateway-${gatewayWord}`; // Use generic like "gateway-two" for others

          const criterionChildren: NavigationItem[] = [];
          const gatewayDetailSource =
            options.currentGatewayReport?.slug === gateway.slug
              ? options.currentGatewayReport
              : gateway;

          // Try to get criterion data from different sources
          let criterionData = null;

          if (gatewayDetailSource?.criterion) {
            criterionData = gatewayDetailSource.criterion;
          } else if (gatewayDetailSource?.criterion_overviews) {
            criterionData = gatewayDetailSource.criterion_overviews;
          } else if (gateway.criterion) {
            criterionData = gateway.criterion;
          } else if (gateway.criterion_overviews) {
            criterionData = gateway.criterion_overviews;
          } else {
            // Fallback to gateway overviews from gradeReport
            const matchingOverview =
              options.gradeReport?.gateway_overviews?.find(
                (gw: any) => gw.slug === gateway.slug,
              );
            if (matchingOverview?.criterion_overviews) {
              criterionData = matchingOverview.criterion_overviews;
            }
          }

          if (criterionData && Array.isArray(criterionData)) {
            criterionData.forEach((criterion: any, index: number) => {
              const indicatorChildren =
                criterion.indicators?.map((indicator: any) => {
                  const indicatorAppend = getIndicatorAppend(indicator);
                  return {
                    title: indicator.label,
                    value: `criterion-${gatewayWord}-${index}-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`,
                    append: indicatorAppend?.text || '',
                    appendClasses: indicatorAppend?.class || '',
                    route: {
                      name: 'report-gateway',
                      params: {
                        id: reportId,
                        grade: options.grade || '',
                        gateway: routeGateway,
                      },
                    },
                    href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                  };
                }) || [];

              criterionChildren.push({
                title: getCriterionDisplayTitle(criterion, criterionData),
                value: `criterion-${gatewayWord}-${index}`,
                isGroup: indicatorChildren.length > 0,
                indentChildren: true,
                children: indicatorChildren,
                route: {
                  name: 'report-gateway',
                  params: {
                    id: reportId,
                    grade: options.grade || '',
                    gateway: routeGateway,
                  },
                },
                href: `#${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                // Auto-open first criterion if this is the current gateway
                defaultOpen:
                  index === 0 &&
                  options.currentGatewayReport?.slug === gateway.slug,
              });
            });
          }

          const gatewayAppend = getGatewayAppend(gateway);
          let defaultOpen = false;

          if (route.name === 'report-gateway' && route.params.gateway) {
            const gatewayParam = route.params.gateway as string;
            if (
              gatewayParam === 'multilingual-learner-supports' &&
              gateway.slug === 'multilingual-learner-supports'
            ) {
              defaultOpen = true;
            } else if (gatewayParam === routeGateway) {
              defaultOpen = true;
            }
          }

          gatewayItems.push({
            title: isMllGateway
              ? 'Multilingual Learner Supports'
              : gateway.label || `Gateway ${gatewayNumber}`,
            value: `gateway-${gatewayWord}`,
            isH4: true,
            isGroup: true,
            borderTop: true,
            defaultOpen,
            href: `#${routeGateway}`,
            children: criterionChildren,
            append: gatewayAppend?.text || '',
            appendClasses: gatewayAppend?.class || '',
          });
        }
      }
      baseItems.push(...gatewayItems);
    }
  }

  return baseItems;
}
